import { useMemo, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Flex, Typography, Spin, Card } from "antd";

import Breadcrumbs from "@/components/ui/Breadcrumbs";
import SectionCustomReportTable from "@/components/tables/SectionCustomReportTable";
import ReportFilters from "./components/ReportFilters";
import ReportActions from "./components/ReportActions";
import ChartSection from "./components/ChartSection";

import { useReportData } from "./hooks/useReportData";
import { useReportGeneration } from "./hooks/useReportGeneration";
import { useReportExport } from "./hooks/useReportExport";

/**
 * Страница отчёта: загрузка конфигурации, применение фильтров,
 * генерация секций, экспорт и печать.
 * Вся бизнес-логика разнесена по хукам и дочерним компонентам.
 */
const Report = () => {
  const { id } = useParams();
  // Локальные значения фильтров по имени поля
  const [filters, setFilters] = useState({});
  // Ключи раскрытых строк таблиц для режима печати
  const [expandedRowKeys, setExpandedRowKeys] = useState(null);
  // Скриншоты отрисованных графиков: {index: dataUrl}
  const [chartScreenshots, setChartScreenshots] = useState({});

  // Загружаем конфигурацию отчёта и справочники
  const {
    data,
    pending,
    statusOptions,
    employeesOptions,
    unitsOptions,
    objectsOptions,
  } = useReportData(id);

  // Генерация секций отчёта по текущим фильтрам
  const {
    sectionsData,
    pending: reportPending,
    generate,
  } = useReportGeneration(id, data);

  // Экспорт отчёта (Excel/CSV)
  const { pending: exportPending, exportReport } = useReportExport(
    id,
    data?.name,
  );

  useEffect(() => {
    // При смене отчёта сбрасываем выбранные фильтры и раскрытие строк
    setFilters({});
    setExpandedRowKeys(null);
  }, [id]);

  useEffect(() => {
    // Инициализация обязательных фильтров значениями по умолчанию
    if (!data || !data.filters || pending) return;

    const requiredFilters = data.filters.filter((filter) => filter.isRequired);
    if (!requiredFilters.length) return;

    const initialValues = {};
    let hasChanges = false;

    requiredFilters.forEach((filter) => {
      switch (filter.type) {
        case "date":
          // Устанавливаем сегодняшнюю дату в формате dayjs
          initialValues[filter.name] = new Date().toISOString().split("T")[0];
          hasChanges = true;
          break;

        case "status":
          // Первый статус из списка
          if (statusOptions.length > 0) {
            initialValues[filter.name] = statusOptions[0].uuid;
            hasChanges = true;
          }
          break;

        case "employee":
          // Первый сотрудник из списка
          if (employeesOptions.length > 0) {
            initialValues[filter.name] = employeesOptions[0].value;
            hasChanges = true;
          }
          break;

        case "unit":
          // Первое подразделение из списка
          if (unitsOptions.length > 0) {
            initialValues[filter.name] = unitsOptions[0].uuid;
            hasChanges = true;
          }
          break;

        case "object":
          // Первый объект из списка для данного definition_uuid
          const objectsList = objectsOptions[filter.object_uuid];
          if (objectsList && objectsList.length > 0) {
            initialValues[filter.name] = objectsList[0].uuid;
            hasChanges = true;
          }
          break;

        default:
          break;
      }
    });

    // Применяем изменения только если есть новые значения
    if (hasChanges) {
      setFilters((prev) => ({ ...prev, ...initialValues }));
    }
  }, [
    data,
    pending,
    statusOptions,
    employeesOptions,
    unitsOptions,
    objectsOptions,
  ]);

  const breadcrumbsItems = useMemo(
    () => [{ title: "Главная" }, { title: "Отчеты" }, { title: data?.name }],
    [data?.name],
  );

  const sectionsWithScreenshots = useMemo(() => {
    if (!sectionsData) return null;

    return sectionsData.map((section, index) =>
      section.type === "chart"
        ? { ...section, screenshot: chartScreenshots[index] }
        : section,
    );
  }, [chartScreenshots, sectionsData]);

  // Обновление отдельного фильтра с сохранением остальных значений
  const handleFilterChange = (name, val) => {
    setFilters((prev) => ({ ...prev, [name]: val ?? null }));
  };

  // Перед печатью раскрываем все строки-родители в таблицах, затем откатываем состояние
  const handlePrint = () => {
    const allExpandedKeys = sectionsData
      .filter((section) => section.type === "table" && section.rows)
      .flatMap((section) =>
        section.rows
          .map((row, idx) => (row.children?.length > 0 ? idx : null))
          .filter((key) => key !== null),
      );

    setExpandedRowKeys(allExpandedKeys);
    setTimeout(() => {
      window.print();
      setExpandedRowKeys(null);
    }, 100);
  };

  const handleChartScreenshot = (index, payload) => {
    setChartScreenshots((prev) => {
      if (prev[index] === payload.dataUrl) return prev;
      return { ...prev, [index]: payload.dataUrl };
    });
  };

  useEffect(() => {
    // Сбрасываем скриншоты при новой генерации секций
    setChartScreenshots({});
  }, [sectionsData]);

  // Рендер секции по типу: текст (HTML) или таблица
  const renderSection = (section, index) => {
    switch (section.type) {
      case "text":
        return (
          // Контент приходит из доверенного источника генерации отчёта
          <div key={index} dangerouslySetInnerHTML={{ __html: section.text }} />
        );
      case "table":
        return (
          <SectionCustomReportTable
            key={index}
            columns={section.columns}
            rows={section.rows}
            sum={section.sum}
            expandedRowKeys={expandedRowKeys}
          />
        );

      case "chart":
        return (
          <ChartSection
            key={index}
            section={section}
            onScreenshot={(payload) => handleChartScreenshot(index, payload)}
          />
        );

      default:
        return null;
    }
  };

  // Глобальный индикатор загрузки конфигурации отчёта
  if (pending) {
    return (
      <div className="spin">
        <Spin size="large" />
      </div>
    );
  }

  // Если данных отчёта нет — ничего не отображаем
  if (!data) return null;

  return (
    <Flex vertical gap={25}>
      <Flex vertical gap={10} className="not-print">
        <Breadcrumbs items={breadcrumbsItems} />
        <Typography.Title level={2} className="no-margin">
          {data.name}
        </Typography.Title>
      </Flex>

      {/* Панель фильтров и запуск генерации отчёта */}
      <ReportFilters
        filters={data.filters}
        filterValues={filters}
        onFilterChange={handleFilterChange}
        onGenerate={() => generate(filters)}
        pending={reportPending}
        statusOptions={statusOptions}
        employeesOptions={employeesOptions}
        unitsOptions={unitsOptions}
        objectsOptions={objectsOptions}
      />

      {reportPending ? (
        // Локальный спиннер во время генерации секций
        <div className="spin" style={{ minHeight: 200 }}>
          <Spin />
        </div>
      ) : (
        sectionsWithScreenshots && (
          <Flex vertical gap={20}>
            {/* Действия над результатом отчёта: экспорт / PDF / печать */}
            <ReportActions
              sectionsData={sectionsWithScreenshots}
              reportName={data.name}
              onExport={(type) => exportReport(type, filters)}
              onPrint={handlePrint}
              exportPending={exportPending}
            />
            <Card
              className="print-card print-no-padding"
              style={{ maxWidth: 1280 }}
            >
              <Flex vertical gap={20}>
                {sectionsWithScreenshots.map(renderSection)}
              </Flex>
            </Card>
          </Flex>
        )
      )}
    </Flex>
  );
};

export default Report;
